import React, { Component } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { confirmCloseAlert } from '../../functions/alerts'
import { getIndexElement } from '../../functions/searchInObject'
import { getInformationWithDataV3 } from '../../services/CABE'
import { getValueCookie } from '../../services/cookieService'
import LoadingWindow from '../../components/LoadingWindow'
import { buildCartLine } from './buildCartLine'

/**
 * SearchUPCV3 — scan/type a UPC or item code, add straight to the SalesOrderV3
 * cart. Rebuilt from scratch (2026-08) as a Sales Order-only component — the
 * original SearchUPC.js is shared across 6 different pages (Picking, Invoice,
 * PurchaseOrder, Inventory, Spoilage, ReturnPO) via a big if/else on
 * ComponentName, which made it hard to follow and meant Sales Order's path was
 * tangled up with five unrelated ones.
 *
 * Looks up /v3/salesorder/item-by-code (RDS-only — itemCatalogue/itemCatalogueAltitude
 * + itemHeader + product_pricing, no live MAS90/WS call) instead of the old
 * /Items/getItemByUpc, which made a live, unguarded WS call to price the item.
 *
 * Tiered customer pricing (2026-08-19): passes the SO's selected customer so
 * the returned standardPrice is already that customer's tier price (default
 * level 1) — see CatalogueV3.js header for the same mechanism.
 */
export default class SearchUPCV3 extends Component {
    constructor(props) {
        super(props)
        this.ModalLoading = React.createRef()
        this.inputId = props.id || uuidv4()
    }

    onKeyDown = async (e) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            const value = e.target.value
            e.target.value = ''
            if (value) await this.searchAndAdd(value)
        }
    }

    async searchAndAdd(rawCode) {
        // Guards against scanning/pressing Enter twice before the first
        // lookup resolves — both calls would otherwise read the same stale
        // Father.state.Products snapshot and could both decide to add a new
        // line for the same item (see CatalogueV3.js's setQuantity for the
        // same race, and salesOrderIntegrity.ts for why this matters).
        if (this._searching) return
        this._searching = true
        this.ModalLoading.current.showState(true)
        try {
            // Some scanners wrap the code as ")N=...=" — same cleanup V2 did.
            let code = rawCode
            if (/(\)([0-9])+=).+/.test(code)) {
                code = code.replace(')', '(').replace('=', ')')
            }

            const Father = this.props.Padre
            const idCompany = getValueCookie('CompanyId')
            const customerNo = Father.state.SalesOrder.Customer.CustomerNo
            const resp = await getInformationWithDataV3('/v3/salesorder/item-by-code', { idCompany, customerNo, code })

            if (!resp || !resp.status || resp.status.code !== 1 || !resp.data) {
                confirmCloseAlert('incorrect', 'The code: ' + rawCode + ' was not found, try with another one')
                return
            }

            const row = resp.data
            const products = Father.state.Products
            const index = getIndexElement(products, 'itemCode', row.itemCode)

            // No standard price configured for this company → block the add,
            // same rule as CatalogueV3.js. Bumping quantity on an item already
            // in the cart is still allowed (it was priced when first added).
            if (index === -1 && (row.standardPrice === null || row.standardPrice === undefined)) {
                confirmCloseAlert('incorrect', row.itemCode + ' has no standard price configured for this company yet. Set it in Price Management first.')
                return
            }

            if (index !== -1) {
                // Already in cart — bump quantity by 1 (same behavior as V2).
                const existing = products[index]
                const newQty = (existing.Visible === 0 ? 0 : Number(existing.quantityOrdered)) + 1
                existing.quantityOrdered = newQty
                existing.totalCost = newQty * Number(existing.UnitPrice)
                existing.Visible = 1
                existing.HaveUPC = true
                products.splice(index, 1)
                products.unshift(existing)
            } else {
                const line = buildCartLine(row, 1, Father.state.SalesOrder.Customer.CustomerName)
                line.HaveUPC = true
                products.unshift(line)
            }

            // setState is async — refreshInputs() reads DOM nodes by id for
            // every row, so it must run after React commits the new row,
            // not immediately after setState() is called (crashes otherwise
            // on the not-yet-rendered row — same bug fixed in CatalogueV3.js).
            Father.setState({ Products: products }, () => {
                Father.calculateTotals()
                Father.refreshInputs()
            })
        } finally {
            this._searching = false
            this.ModalLoading.current.showState(false)
        }
    }

    render() {
        const width = this.props.width ? `${this.props.width}%` : '100%'
        return (
            <div className="input-group" style={{ width, marginBottom: 0 }}>
                <LoadingWindow ref={this.ModalLoading} />
                <input id={this.inputId} disabled={!!this.props.disabled} type="text" autoComplete="off"
                    placeholder="Search by item code..." className="form-control search-input" onKeyDown={this.onKeyDown} />
                <button className="btn btn-primary" type="button">Search</button>
            </div>
        )
    }
}
